import _regeneratorRuntime from 'babel-runtime/regenerator';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';
import _extends from 'babel-runtime/helpers/extends';

import _ from 'lodash';
import { mapState } from 'vuex';
import { ElSchemaFormItem } from 'el-schema-form';
import ColorPicker from '@com/components/color-picker/index.vue';
import FontLayer from '@com/components/font-layer/index.vue';
import { exchangeRgbaObjectToRgba, exchangeRgbObjectToRgb } from '@com/utils/color';
import { defaultFont } from '@/store/creative/template/config';
import PtButton from '@com/pt-components/pt-button/index.vue';
import UploaderBar from '@com/components/asset-uploader/uploader-bar.vue';
import { maxFontSize } from '@com/utils/file-max-size';
import { localLanguage } from '@/common/storage';

export default {
  components: {
    ElSchemaFormItem: ElSchemaFormItem,
    ColorPicker: ColorPicker,
    FontLayer: FontLayer,
    PtButton: PtButton,
    UploaderBar: UploaderBar
  },

  props: {
    uiSchema: {
      type: Array,
      required: true
    },

    model: {
      type: Object,
      required: true
    },

    popoverVisible: {
      type: Boolean,
      default: false
    }
  },

  data: function data() {
    return {
      formConfig: {
        labelPosition: 'left',
        size: 'small',
        inline: false
      },
      activeColorType: 'single',
      startColor: '#ff0000',
      endColor: '#000000',
      colorPickerVisible: false,
      fontSelectorLoading: false,
      assetUploaderConfig: {
        accepts: ['ttf', 'otf'],
        maxSize: maxFontSize * 1024 * 1024,
        type: 'font',
        activeRules: ['' + this.$t('basic.upload.fontWaring', {
          type: 'ttf,otf',
          size: maxFontSize + 'M'
        })],
        loading: false,
        disabled: false
      }
    };
  },


  computed: _extends({}, mapState({
    isCommonTemplateCustom: function isCommonTemplateCustom(state) {
      return state.creative.template.isCommonTemplateCustom;
    },
    assetFontList: function assetFontList(state) {
      return state.assets.resource.assetFontList;
    },
    isCocosEngine: function isCocosEngine(state) {
      return state.creative.template.isCocosEngine;
    }
  }), {
    colorTypes: function colorTypes() {
      return this.hasPermission('i18n.gradient') ? ['single', 'gradient'] : ['single'];
    },
    fontSelectorConfig: function fontSelectorConfig() {
      return function (options) {
        var AssetFontList = this.isCommonTemplateCustom ? this.assetFontList : [];
        return {
          prop: 'font',
          field: {
            options: options.concat(AssetFontList),
            clearable: true
          }
        };
      };
    },
    lang: function lang() {
      switch (localLanguage.get()) {
        case 'en':
          return 'en';
        case 'ja':
          return 'ja';
        default:
          return 'zh';
      }
    },
    className: function className() {
      return 'app-lang-' + this.lang;
    }
  }),

  watch: {
    popoverVisible: function popoverVisible(val) {
      if (!val) {
        // popover 关闭时关闭颜色选择器
        this.colorPickerVisible = false;
      } else {
        // 监听 字体渐变色/单色
        if (this.hasPermission('i18n.gradient')) {
          this.activeColorType = this.model.style.gradient ? 'gradient' : 'single';
        }
      }
    }
  },

  mounted: function mounted() {
    if (this.hasPermission('i18n.gradient')) {
      // 如果开启了渐变色，默认使用渐变色
      if (this.model.style.gradient) {
        this.activeColorType = 'gradient';
        _.set(this.model, 'style.startColor', this.model.style.startColor || this.startColor);
        _.set(this.model, 'style.endColor', this.model.style.endColor || this.endColor);
        _.set(this.model, 'style.gradient', true);
        this.$emit('submit', this.model);
      }
    }
  },


  methods: {
    validateField: function validateField() {
      return this.$refs.form.validateField.apply(null, arguments);
    },
    handleChange: function handleChange(_ref) {
      var prop = _ref.prop,
          value = _ref.value;

      // 如果是字体字段，则将其设置为 Arial，代表使用默认字体
      // languagesMgr 需要判断 font 字段有值，才会执行设置字体
      // ps 产出的素材区分横竖屏，当切换横竖屏的时候，languageMgr 的判断逻辑是，如果无 font 字段或值为空，则切换横竖屏的时候不会更换字体
      // 所以这里将字体设置为 Arial，以便产出的素材能够正常显示
      if (prop === 'font' && !value) {
        value = defaultFont;
      }
      _.set(this.model, 'style.' + prop, value);
      this.$emit('submit', this.model);
    },
    handleSwitchColorType: function handleSwitchColorType(val) {
      if (this.activeColorType === val) return;
      this.activeColorType = val;
      if (val === 'gradient') {
        _.set(this.model, 'style.startColor', this.model.style.startColor || this.startColor);
        _.set(this.model, 'style.endColor', this.model.style.endColor || this.endColor);
        _.set(this.model, 'style.gradient', true);
      } else {
        _.set(this.model, 'style.gradient', false);
      }
      this.$emit('submit', this.model);
    },
    handleTextFontColorChange: function handleTextFontColorChange(val) {
      var rgba = val.rgba;
      // color 属性用 rgb 格式存储

      _.set(this.model, 'style.color', exchangeRgbObjectToRgb(rgba));
      _.set(this.model, 'style.gradient', false);
      _.set(this.model, 'style.startColor', '');
      _.set(this.model, 'style.endColor', '');
      this.$emit('submit', this.model);
    },
    handleChangeStartColor: function handleChangeStartColor(val) {
      var rgba = val.rgba;

      _.set(this.model, 'style.startColor', exchangeRgbaObjectToRgba(rgba));
      _.set(this.model, 'style.gradient', true);
      this.$emit('submit', this.model);
    },
    handleChangeEndColor: function handleChangeEndColor(val) {
      var rgba = val.rgba;

      _.set(this.model, 'style.endColor', exchangeRgbaObjectToRgba(rgba));
      _.set(this.model, 'style.gradient', true);
      this.$emit('submit', this.model);
    },


    // color-picker 颜色变化回调函数
    handleColorChange: function handleColorChange(val, prop) {
      var rgba = val.rgba;

      if (this.isCocosEngine) {
        _.set(this.model, 'style.' + prop, exchangeRgbObjectToRgb(rgba));
      } else {
        _.set(this.model, 'style.' + prop, exchangeRgbaObjectToRgba(rgba));
      }
      this.$emit('submit', this.model);
    },
    updateFont: function updateFont(_ref2) {
      var prop = _ref2.prop,
          value = _ref2.value;

      this.handleChange({
        prop: prop,
        value: value.postscript_name === 'system-font' ? defaultFont : value.postscript_name
      });
    },
    applyFontToOtherTextNode: function applyFontToOtherTextNode() {
      this.$emit('apply-submit', this.model);
    },
    uploadComplete: function uploadComplete() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                // this.$message.success(this.$t('basic.upload.fileStatus.success'))
                _this.$store.dispatch('assets/resource/getAssetFontList');
                _context.next = 3;
                return _this.$refs.fontLayerRef[0].uploadComplete();

              case 3:
              case 'end':
                return _context.stop();
            }
          }
        }, _callee, _this);
      }))();
    }
  }
};